import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveChat } from '@/contexts/ActiveChatContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { ChatMessage, ChatWithDetails } from '@/types/chat';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface ChatsContextValue {
  chats: ChatWithDetails[];
  activeChats: ChatWithDetails[];
  archivedChats: ChatWithDetails[];
  loading: boolean;
  error: string | null;
  totalUnreadCount: number;
  archiveChat: (chatId: string) => Promise<void>;
  unarchiveChat: (chatId: string) => Promise<void>;
  deleteChat: (chatId: string) => Promise<void>;
  refetch: () => Promise<void>;
}

const ChatsContext = createContext<ChatsContextValue | null>(null);

export function ChatsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { getActiveChatId } = useActiveChat();
  const [chats, setChats] = useState<ChatWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchChats = useCallback(async () => {
    if (!user) {
      setChats([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch all chats where user is a participant and their hidden flag is false
      const { data: chatsData, error: chatsError } = await supabase
        .from('chats')
        .select('*')
        .or(
          `and(participant_1.eq.${user.id},hidden_by_participant_1.eq.false),and(participant_2.eq.${user.id},hidden_by_participant_2.eq.false)`
        )
        .order('last_message_at', { ascending: false });

      if (chatsError) throw chatsError;

      if (!chatsData || chatsData.length === 0) {
        setChats([]);
        setLoading(false);
        return;
      }

      // Get all notification IDs and participant IDs
      const notificationIds = chatsData.map(c => c.notification_id);
      const participantIds = new Set<string>();
      chatsData.forEach(c => {
        participantIds.add(c.participant_1);
        participantIds.add(c.participant_2);
      });

      // Fetch notifications with related data
      const { data: notificationsData } = await supabase
        .from('notifications')
        .select(`
          id,
          message,
          collection_id,
          item_id
        `)
        .in('id', notificationIds);

      // Fetch collection names
      const collectionIds = notificationsData?.filter(n => n.collection_id).map(n => n.collection_id as string) || [];
      const { data: collectionsData } = collectionIds.length > 0
        ? await supabase.from('collections').select('id, name').in('id', collectionIds)
        : { data: [] as { id: string; name: string }[] };

      // Fetch item names
      const itemIds = notificationsData?.filter(n => n.item_id).map(n => n.item_id as string) || [];
      const { data: itemsData } = itemIds.length > 0
        ? await supabase.from('collection_items').select('id, name').in('id', itemIds)
        : { data: [] as { id: string; name: string }[] };

      // Fetch profiles
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, display_name, avatar_url')
        .in('id', Array.from(participantIds));

      // Fetch last message for each chat
      const chatIds = chatsData.map(c => c.id);
      const { data: messagesData } = await supabase
        .from('chat_messages')
        .select('*')
        .in('chat_id', chatIds)
        .order('created_at', { ascending: false });

      // Fetch unread counts
      const { data: unreadData } = await supabase
        .from('chat_messages')
        .select('chat_id')
        .in('chat_id', chatIds)
        .neq('sender_id', user.id)
        .eq('is_read', false);

      // Build lookup maps
      const notificationMap = new Map(notificationsData?.map(n => [n.id, n] as const) || []);
      const collectionMap = new Map((collectionsData || []).map(c => [c.id, c] as const));
      const itemMap = new Map((itemsData || []).map(i => [i.id, i] as const));
      const profileMap = new Map(profilesData?.map(p => [p.id, p] as const) || []);
      
      // Group messages by chat and get the first (latest) one
      const lastMessageMap = new Map<string, ChatMessage>();
      messagesData?.forEach(m => {
        if (!lastMessageMap.has(m.chat_id)) {
          lastMessageMap.set(m.chat_id, m as ChatMessage);
        }
      });

      // Count unread messages per chat
      const unreadCountMap = new Map<string, number>();
      unreadData?.forEach(u => {
        unreadCountMap.set(u.chat_id, (unreadCountMap.get(u.chat_id) || 0) + 1);
      });

      // Build enriched chat objects
      const enrichedChats: ChatWithDetails[] = chatsData.map(chat => {
        const isParticipant1 = chat.participant_1 === user.id;
        const otherUserId = isParticipant1 ? chat.participant_2 : chat.participant_1;
        const otherProfile = profileMap.get(otherUserId);
        const notification = notificationMap.get(chat.notification_id);
        const collection = notification?.collection_id ? collectionMap.get(notification.collection_id) : null;
        const item = notification?.item_id ? itemMap.get(notification.item_id) : null;

        // Per-user archive/hidden status
        const isArchivedByMe = isParticipant1 
          ? chat.archived_by_participant_1 
          : chat.archived_by_participant_2;
        const isArchivedByOther = isParticipant1 
          ? chat.archived_by_participant_2 
          : chat.archived_by_participant_1;
        const isHiddenByOther = isParticipant1 
          ? chat.hidden_by_participant_2 
          : chat.hidden_by_participant_1;

        return {
          ...chat,
          status: chat.status as 'active' | 'archived',
          otherUser: {
            id: otherUserId,
            displayName: otherProfile?.display_name || null,
            avatarUrl: otherProfile?.avatar_url || null,
          },
          notification: {
            id: notification?.id || '',
            message: notification?.message || null,
            collection: collection ? { id: collection.id, name: collection.name } : null,
            item: item ? { id: item.id, name: item.name } : null,
          },
          lastMessage: lastMessageMap.get(chat.id),
          unreadCount: unreadCountMap.get(chat.id) || 0,
          isArchivedByMe,
          isArchivedByOther,
          isHiddenByOther,
        };
      });

      setChats(enrichedChats);
    } catch (err) {
      console.error('Error fetching chats:', err);
      setError('Failed to load chats');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Get total unread count across all chats
  const totalUnreadCount = chats.reduce((sum, chat) => sum + chat.unreadCount, 0);

  // Separate active and archived chats (per-user)
  const activeChats = chats.filter(c => !c.isArchivedByMe);
  const archivedChats = chats.filter(c => c.isArchivedByMe);

  // Track which message IDs we've already processed to prevent double-counting
  const processedReadMessagesRef = useRef<Set<string>>(new Set());

  // Set up realtime subscriptions
  useEffect(() => {
    if (!user) return;

    fetchChats();

    let chatsChannel: RealtimeChannel;
    let messagesChannel: RealtimeChannel;

    // Handle new message - optimistic update
    const handleNewMessage = (payload: any) => {
      const newMessage = payload.new as ChatMessage;
      
      // Ignore messages sent by the current user
      if (newMessage.sender_id === user.id) {
        // Still update lastMessage for our own messages
        setChats(prev => prev.map(chat => 
          chat.id === newMessage.chat_id
            ? { ...chat, lastMessage: newMessage, last_message_at: newMessage.created_at }
            : chat
        ));
        return;
      }
      
      const activeChatId = getActiveChatId();
      
      // Check if the chat exists in our local state
      setChats(prev => {
        const chatExists = prev.some(chat => chat.id === newMessage.chat_id);
        
        if (!chatExists) {
          // New chat we don't have - trigger a refetch to get it
          fetchChats();
          return prev;
        }
        
        // If this chat is currently active, don't update unread count
        if (newMessage.chat_id === activeChatId) {
          return prev.map(chat => 
            chat.id === newMessage.chat_id
              ? { ...chat, lastMessage: newMessage, last_message_at: newMessage.created_at }
              : chat
          );
        }
        
        // For other chats, increment unread and update lastMessage
        return prev.map(chat => 
          chat.id === newMessage.chat_id
            ? { 
                ...chat, 
                lastMessage: newMessage, 
                last_message_at: newMessage.created_at,
                unreadCount: chat.unreadCount + 1 
              }
            : chat
        );
      });
    };

    // Handle read status updates - decrement unread count locally
    const handleMessageUpdate = (payload: any) => {
      const updatedMessage = payload.new as ChatMessage;
      
      // Only care about messages that are now marked as read
      if (updatedMessage.is_read) {
        // Check if we've already processed this message
        if (processedReadMessagesRef.current.has(updatedMessage.id)) {
          return;
        }
        
        // Mark as processed
        processedReadMessagesRef.current.add(updatedMessage.id);
        
        // Clean up old entries after a delay
        setTimeout(() => {
          processedReadMessagesRef.current.delete(updatedMessage.id);
        }, 5000);
        
        setChats(prev => prev.map(chat => 
          chat.id === updatedMessage.chat_id && chat.unreadCount > 0
            ? { ...chat, unreadCount: chat.unreadCount - 1 }
            : chat
        ));
      }
    };

    // Handle chat changes
    const handleChatChange = (payload: any) => {
      // Only refetch for new chats (INSERT)
      if (payload.eventType === 'INSERT') {
        fetchChats();
        return;
      }
      
      // For UPDATE, update local state optimistically
      if (payload.eventType === 'UPDATE') {
        const updatedChat = payload.new;
        const isParticipant1 = updatedChat.participant_1 === user.id;
        
        // Check if this chat should now be hidden from us
        const isHiddenByMe = isParticipant1 
          ? updatedChat.hidden_by_participant_1 
          : updatedChat.hidden_by_participant_2;
        
        if (isHiddenByMe) {
          // Remove from local state
          setChats(prev => prev.filter(c => c.id !== updatedChat.id));
          return;
        }
        
        // Update archive status
        const isArchivedByMe = isParticipant1 
          ? updatedChat.archived_by_participant_1 
          : updatedChat.archived_by_participant_2;
        const isArchivedByOther = isParticipant1 
          ? updatedChat.archived_by_participant_2 
          : updatedChat.archived_by_participant_1;
        const isHiddenByOther = isParticipant1 
          ? updatedChat.hidden_by_participant_2 
          : updatedChat.hidden_by_participant_1;
        
        setChats(prev => prev.map(c => 
          c.id === updatedChat.id 
            ? { ...c, isArchivedByMe, isArchivedByOther, isHiddenByOther }
            : c
        ));
      }
    };

    const setupSubscription = async () => {
      chatsChannel = supabase
        .channel('chats-context-realtime')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'chats',
          },
          handleChatChange
        )
        .subscribe();

      messagesChannel = supabase
        .channel('chat-messages-context-realtime')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'chat_messages',
          },
          handleNewMessage
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'chat_messages',
          },
          handleMessageUpdate
        )
        .subscribe();
    };

    setupSubscription();

    return () => {
      if (chatsChannel) supabase.removeChannel(chatsChannel);
      if (messagesChannel) supabase.removeChannel(messagesChannel);
    };
  }, [user, fetchChats, getActiveChatId]);

  // Archive chat for current user only
  const archiveChat = async (chatId: string) => {
    if (!user) return;

    let chat = chats.find(c => c.id === chatId);
    
    if (!chat) {
      const { data } = await supabase
        .from('chats')
        .select('participant_1, participant_2')
        .eq('id', chatId)
        .maybeSingle();
      
      if (!data) return;
      chat = data as any;
    }

    const isParticipant1 = chat.participant_1 === user.id;
    const updateData = isParticipant1
      ? { archived_by_participant_1: true }
      : { archived_by_participant_2: true };

    // Optimistic update
    setChats(prev => prev.map(c => 
      c.id === chatId ? { ...c, isArchivedByMe: true } : c
    ));

    const { error } = await supabase
      .from('chats')
      .update(updateData)
      .eq('id', chatId);

    if (error) {
      // Revert on error
      setChats(prev => prev.map(c => 
        c.id === chatId ? { ...c, isArchivedByMe: false } : c
      ));
      toast({
        title: 'Error',
        description: 'Failed to archive chat.',
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Chat archived',
      description: 'This conversation has been archived.',
    });
  };

  // Unarchive chat for current user
  const unarchiveChat = async (chatId: string) => {
    if (!user) return;

    let chat = chats.find(c => c.id === chatId);
    
    if (!chat) {
      const { data } = await supabase
        .from('chats')
        .select('participant_1, participant_2')
        .eq('id', chatId)
        .maybeSingle();
      
      if (!data) return;
      chat = data as any;
    }

    const isParticipant1 = chat.participant_1 === user.id;
    const updateData = isParticipant1
      ? { archived_by_participant_1: false }
      : { archived_by_participant_2: false };

    // Optimistic update
    setChats(prev => prev.map(c => 
      c.id === chatId ? { ...c, isArchivedByMe: false } : c
    ));

    const { error } = await supabase
      .from('chats')
      .update(updateData)
      .eq('id', chatId);

    if (error) {
      // Revert on error
      setChats(prev => prev.map(c => 
        c.id === chatId ? { ...c, isArchivedByMe: true } : c
      ));
      toast({
        title: 'Error',
        description: 'Failed to unarchive chat.',
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Chat unarchived',
      description: 'This conversation is now active.',
    });
  };

  // Delete (hide) chat for current user only
  const deleteChat = async (chatId: string) => {
    if (!user) return;

    let chat = chats.find(c => c.id === chatId);
    
    if (!chat) {
      const { data } = await supabase
        .from('chats')
        .select('participant_1, participant_2')
        .eq('id', chatId)
        .maybeSingle();
      
      if (!data) return;
      chat = data as any;
    }

    const isParticipant1 = chat.participant_1 === user.id;
    const updateData = isParticipant1
      ? { hidden_by_participant_1: true }
      : { hidden_by_participant_2: true };

    // Optimistic update - remove from list
    const previousChats = chats;
    setChats(prev => prev.filter(c => c.id !== chatId));

    const { error } = await supabase
      .from('chats')
      .update(updateData)
      .eq('id', chatId);

    if (error) {
      // Revert on error
      setChats(previousChats);
      toast({
        title: 'Error',
        description: 'Failed to delete chat.',
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Chat deleted',
      description: 'This conversation has been removed.',
    });
  };

  return (
    <ChatsContext.Provider
      value={{
        chats,
        activeChats,
        archivedChats,
        loading,
        error,
        totalUnreadCount,
        archiveChat,
        unarchiveChat,
        deleteChat,
        refetch: fetchChats,
      }}
    >
      {children}
    </ChatsContext.Provider>
  );
}

export function useChatsContext() {
  const context = useContext(ChatsContext);
  if (!context) {
    throw new Error('useChatsContext must be used within a ChatsProvider');
  }
  return context;
}
